"use client";

import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'sonner';
import { useRouter } from "next/navigation";
import { useSession, useUser } from '@clerk/nextjs';
import { DataGrid, DataGridColumnHeader, TDataGridRequestParams, useDataGrid } from '../../common/data-grid';
import { ScreenLoader } from '../../common/loaders/ScreenLoader';
import { addNewLicense, fetchT3headlessData } from '@/lib/action';
import { KeenIcon } from '../../common/keenicons';
import { formatIsoDate } from '@/app/utils/Date';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../common/ui/select';

const T3headlessLists = ({ total, permission, membership }: any) => {
    const { formatMessage } = useIntl();
    const router = useRouter();
    const { user, isLoaded: userLoaded } = useUser();
    const { session, isLoaded: sessionLoaded } = useSession();
    const intl = useIntl();
    const isReady = user?.id && session?.id;
    const [addLoading, setAddLoading] = useState<any>(false);
    const [isDisabled, setIsDisabled] = useState<any>(true);
    const [memberships, setMemberships] = useState<any[]>([]);
    const [selectedMembership, setSelectedMembership] = useState<string>('0');

    useEffect(() => {
        if (isReady) {
          const membershipList = membership?.map((item: any) => ({
                    id: item?.data?.id,
                    title: `#${item?.data?.id} ${item?.data?.membership?.title ?? ''}`
                }));
                 setMemberships(membershipList);
        }
    }, [isReady]);

    useEffect(() => {
        if (userLoaded && !user?.id) {
            router.push('/sign-in');
            toast.error(intl.formatMessage({ id: 'USER_ID_MISSING' }));
        }

        if (sessionLoaded && !session?.id) {
            router.push('/sign-in');
            toast.error(intl.formatMessage({ id: 'SESSION_ID_MISSING' }));
        }

    }, [userLoaded, sessionLoaded, user, session]);


    const columns = useMemo<ColumnDef<any>[]>(
        () => [
            {
                accessorKey: 'id',
                header: ({ column }) => <DataGridColumnHeader title={formatMessage({ id: 'ID', })} column={column} />,
                cell: (info) => info.row.original.id,
                enableSorting: false,
                enableHiding: false,
                meta: {
                    headerTitle: formatMessage({ id: 'ID' }),
                }
            },
            {
                accessorKey: 'license_key',
                header: ({ column }) => <DataGridColumnHeader title={formatMessage({ id: 'LICENSE_KEY', })} column={column} />,
                cell: (info) => (
                    <div className='text-body text-sm transition-colors  '>
                        <span title={info.row.original.license_key} className="flex items-center gap-1">
                            {info.row.original.license_key}
                            <span
                                className="btn btn-sm btn-icon btn-clear text-gray-500 hover:text-primary-active cursor-pointer"
                                onClick={() => {
                                    navigator.clipboard.writeText(info.row.original.license_key);
                                    toast.success(formatMessage({ id: "LICENSE_KEY_COPIED" }));
                                }}
                            >
                                <KeenIcon icon="copy" />
                            </span>
                        </span>
                    </div>
                ),
                enableSorting: false,
                enableHiding: false,
                meta: {
                    headerTitle: formatMessage({ id: 'LICENSE_KEY' }),
                }
            },
            {
                accessorKey: 'domains',
                header: ({ column }) => <DataGridColumnHeader title={formatMessage({ id: 'DOMAINS', })} column={column} />,
                cell: (info) => info.row.original.domains ?? "-",
                enableSorting: false,
                enableHiding: false,
                meta: {
                    headerTitle: formatMessage({ id: 'DOMAINS' }),
                }
            },

            {
                accessorFn: (row) => row.created_at,
                id: 'created_at',
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: "CREATED_AT" })}
                        column={column} />
                ),
                enableSorting: false,
                enableHiding: false,
                cell: (info) => formatIsoDate(info.row.original.created_at),
                meta: {
                    headerTitle: formatMessage({ id: "CREATED_AT" }),
                }
            },
            {
                accessorFn: (row) => row.expiry,
                id: 'expiry',
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: "EXPIRY_DATE" })}
                        column={column} />
                ),
                enableSorting: false,
                enableHiding: false,
                cell: (info) => formatIsoDate(info.row.original.expiry),
                meta: {
                    headerTitle: formatMessage({ id: "EXPIRY_DATE" }),
                }
            },
            {
                accessorFn: (row) => row.support,
                id: 'support',
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: "SUPPORT_END_DATE" })}
                        column={column} />
                ),
                enableSorting: false,
                enableHiding: false,
                cell: (info) => formatIsoDate(info.row.original.support),
                meta: {
                    headerTitle: formatMessage({ id: "SUPPORT_END_DATE" }),
                }
            },
            {
                accessorFn: (row) => row.is_active,
                id: 'is_active',
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: "STATUS" })}
                        column={column} />
                ),
                enableSorting: false,
                enableHiding: false,
                cell: (info) => {
                    if (info.row.original.is_block) {
                        return (
                            <span className={`badge badge-danger shrink-0 badge-outline rounded-[30px]`}>
                                <span className={`size-1.5 rounded-full bg-danger me-1.5`}></span>
                                <FormattedMessage id="BLOCK" />
                            </span>
                        )
                    } else {
                        return (
                            <span className={`badge badge-success shrink-0 badge-outline rounded-[30px]`}>
                                <span className={`size-1.5 rounded-full bg-success me-1.5`}></span>

                                <FormattedMessage id="ACTIVE" />
                            </span>
                        )
                    }
                },
                meta: {
                    headerTitle: formatMessage({ id: "STATUS" }),
                }
            },
        ],
        [formatMessage]
    );

    if (!userLoaded && !sessionLoaded) {
        return (<ScreenLoader />)
    }

    if (user == undefined || user == null || session == undefined || session == null) {
        return (<ScreenLoader />)
    }


    useEffect(() => {
        if (addLoading == true) {
            setIsDisabled(true);
        } else if (permission.t3headless == 1) {
            if (permission?.t3headless_config?.add == false) {
                setIsDisabled(true);
            } else {
                setIsDisabled(false);
            }
        } else if (permission.t3headless == 0) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
    }, [addLoading, permission]);

    const fetchT3headless = async (params: TDataGridRequestParams) => {
        const queryParams = new URLSearchParams();
        try {
            if (!user?.id) {
                toast.error(intl.formatMessage({ id: 'USER_ID_MISSING' }));
                return {
                    data: [],
                    totalCount: 0
                };
            }

            if (!session?.id) {
                toast.error(intl.formatMessage({ id: 'SESSION_ID_MISSING' }));
                return {
                    data: [],
                    totalCount: 0
                };
            }

            const membershipStr = typeof selectedMembership === "string" ? selectedMembership : String(selectedMembership);

            if (membershipStr.trim() !== "" && membershipStr !== "0") {
                queryParams.set("membership_id", membershipStr.trim());
            }

            let _res = await fetchT3headlessData(user?.id, session?.id, queryParams.toString());

            if (_res?.data?.error || _res?.error) {
                toast.error(_res?.data?.error ? _res?.data?.error : _res?.error || intl.formatMessage({ id: 'UNEXPECTED_ERROR' }));

            } else {
                return {
                    data: _res?.data?.licenses, // Server response data
                    totalCount: total ?? _res?.data?.license // Total count for pagination
                };
            }

        } catch (error) {
            toast(formatMessage({ id: 'FAILED_TO_FETCH_T3HEADLESS_DATA' }));

            return {
                data: [],
                totalCount: 0
            };
        }

    };


    const Listbar = ({ }: any) => {
        const { table } = useDataGrid();
        const updateTable = () => {
            table.setColumnFilters(
                table.getState().columnFilters.filter((filter) => filter.id !== 'query') // Exclude the filter with id 'query'
            );
        }


        const addT3headless = async () => {
            const data = {
                "product_key": "t3headless"
            }
            setAddLoading(true);
            try {
                const _res: any = await addNewLicense(data, user?.id, session?.id);
                if (_res?.data?.message || _res?.message) {
                    toast.error(_res?.data?.message || _res?.message || _res?.data?.msg || _res?.msg || _res?.data?.err || _res?.err || _res?.data?.error || _res?.error || intl.formatMessage({ id: 'UNEXPECTED_ERROR' }));
                } else if (_res?.data?.error || _res?.error) {
                    toast.error(_res?.data?.message || _res?.message || _res?.data?.msg || _res?.msg || _res?.data?.err || _res?.err || _res?.data?.error || _res?.error || intl.formatMessage({ id: 'UNEXPECTED_ERROR' }));
                } else {
                    toast.success(formatMessage({ id: 'NEW_T3HEADLESS_CREATED_SUCCESSFULLY' }))
                }
            } catch (err: any) {
                toast.error(err?.message || intl.formatMessage({ id: 'MEMBERSHIP.ERROR' }));
            } finally {
                setAddLoading(false);
                updateTable();
            }
        };

        const filter = (value: any) => {
            if (typeof value == "string") {
                table.setPageIndex(0);
                if (value.trim() === '') {
                    table.setColumnFilters(
                        table.getState().columnFilters.filter((filter) => filter.id !== 'query')
                    );
                } else {
                    table.setColumnFilters([
                        ...table.getState().columnFilters.filter((filter) => filter.id !== 'query'),
                        { id: 'query', value: value },
                    ]);
                }
            } else {
                table.setColumnFilters(
                    table.getState().columnFilters.filter((filter) => filter.id !== 'query')
                );
            }
        };

        return (
            <div className="card-header border-b-0 px-5">
                <h3 className="card-title"><FormattedMessage id="T3HEADLESS" /></h3>

                <div className='flex flex-row gap-3 items-end justify-end '>
                    <div>
                        <Select defaultValue={selectedMembership} onValueChange={(value) => {
                            setSelectedMembership(value);
                            filter(value);
                        }}>
                            <SelectTrigger className="min-w-32" size="sm">
                                <SelectValue placeholder={formatMessage({ id: 'SELECT' })} />
                            </SelectTrigger>
                            <SelectContent className="min-w-32">
                                <SelectItem value="0">
                                    <FormattedMessage id="ALL_MEMBERSHIPS" />
                                </SelectItem>
                                {memberships?.map((membership: any) => (
                                    <SelectItem key={membership.id} value={String(membership.id)}>
                                        {membership.title}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <button
                        className={`cursor-pointer btn btn-sm btn-primary`}
                        disabled={isDisabled}
                        onClick={() => addT3headless()}>
                        <i className="ki-filled ki-plus"></i>{addLoading ? <FormattedMessage id="PLEASE_WAIT" /> : <FormattedMessage id="NEW_LICENSE" />}
                    </button>
                </div>


            </div>
        );
    };

    return (
        <Fragment>
            <DataGrid
                columns={columns}
                serverSide={true}
                onFetchData={fetchT3headless}
                rowSelection={true}
                getRowId={(row: any) => row.id}
                pagination={{ size: total }}
                layout={{
                    card: true,
                    classes: {
                        container: 'd-none-data-pagination'
                    }
                }}
                toolbar={<Listbar />}
            />
        </Fragment>
    );
};

export default T3headlessLists;